import { useState, useEffect, useCallback } from "react";
import { X, RotateCcw, ChevronRight, Play } from "lucide-react";

const fonts = {
  heading: "'Cabinet Grotesk', sans-serif",
  body: "'Manrope', sans-serif",
};

// ─── Scene definitions (~58s total) ───
const SCENES = [
  {
    id: "intro",
    duration: 3000,
    image: null,
    caption: "The complete Yestimate workflow",
    sub: "No software to install. Online access wherever you are.",
    cursor: [],
    isMobile: false,
  },
  {
    id: "tech-dashboard",
    duration: 5000,
    image: "/walkthrough/01_tech_dashboard.jpg",
    caption: "Technician sees their assigned jobs",
    sub: "Clean list. Accept and start inspecting. Intuitive for any tech.",
    cursor: [
      { at: 800, x: 42, y: 42 },
      { at: 2000, x: 42, y: 42, click: true },
    ],
    isMobile: false,
  },
  {
    id: "tech-vi-section",
    duration: 4500,
    image: "/walkthrough/02_tech_vi_section.jpg",
    caption: "Guided inspection — Under-Bonnet checks",
    sub: "Every section structured. Every item covered. Nothing missed.",
    cursor: [
      { at: 600, x: 32, y: 52 },
      { at: 1500, x: 32, y: 52, click: true },
    ],
    isMobile: false,
  },
  {
    id: "battery-test",
    duration: 6000,
    image: "/walkthrough/03_battery_test.jpg",
    caption: "Technician captures battery test — classified RED",
    sub: "Measurement values, test photo, classification — all guided.",
    cursor: [
      { at: 500, x: 17, y: 27 },
      { at: 1200, x: 17, y: 27, click: true },
      { at: 2500, x: 50, y: 45 },
      { at: 3500, x: 50, y: 60 },
    ],
    isMobile: false,
  },
  {
    id: "tech-review",
    duration: 5000,
    image: "/walkthrough/04_tech_review.jpg",
    caption: "Technician reviews and submits to Front of House",
    sub: "Complete summary before submission. Clear. Fast.",
    cursor: [
      { at: 2000, x: 40, y: 80 },
      { at: 3500, x: 40, y: 80, click: true },
    ],
    isMobile: false,
  },
  {
    id: "foh-dashboard",
    duration: 5000,
    image: "/walkthrough/05_foh_dashboard.jpg",
    caption: "FOH Status Board — see what needs attention",
    sub: "No chasing. No confusion. One dashboard.",
    cursor: [
      { at: 1000, x: 12, y: 22 },
      { at: 2200, x: 12, y: 22, click: true },
    ],
    isMobile: false,
  },
  {
    id: "foh-action",
    duration: 4500,
    image: "/walkthrough/06_foh_action.jpg",
    caption: "FOH Action Required — RED and AMBER items flagged",
    sub: "Priority items visible instantly. Click to review and price.",
    cursor: [
      { at: 800, x: 37, y: 50 },
      { at: 2000, x: 37, y: 50, click: true },
    ],
    isMobile: false,
  },
  {
    id: "call-companion",
    duration: 5000,
    image: "/walkthrough/08_call_companion.jpg",
    caption: "Call Companion — customer communication at a glance",
    sub: "Tech notes, priority items, evidence — ready for the phone call.",
    cursor: [
      { at: 800, x: 50, y: 35 },
      { at: 2500, x: 50, y: 55 },
    ],
    isMobile: false,
  },
  {
    id: "pricing",
    duration: 5500,
    image: "/walkthrough/09_pricing.jpg",
    caption: "Pricing & Scope Builder — inspection findings to priced jobs",
    sub: "Inspection items on the left. Service recommendations built in.",
    cursor: [
      { at: 800, x: 15, y: 35 },
      { at: 2000, x: 15, y: 35, click: true },
      { at: 3500, x: 55, y: 50 },
    ],
    isMobile: false,
  },
  {
    id: "pricing-jce",
    duration: 5500,
    image: "/walkthrough/10_pricing_jce.jpg",
    caption: "Job Intelligence — linked items and JCE suggestions",
    sub: "System knows discs and pads go together. Complete, correctly-priced jobs.",
    cursor: [
      { at: 800, x: 78, y: 40 },
      { at: 2500, x: 78, y: 55 },
      { at: 4000, x: 50, y: 88 },
      { at: 4500, x: 50, y: 88, click: true },
    ],
    isMobile: false,
  },
  {
    id: "send-portal",
    duration: 5000,
    image: "/walkthrough/11_send_portal.jpg",
    caption: "Send to Customer — one click, professional communication",
    sub: "100% ready. Choose communication style. Send.",
    cursor: [
      { at: 1500, x: 50, y: 50 },
      { at: 3000, x: 50, y: 70 },
      { at: 3500, x: 50, y: 70, click: true },
    ],
    isMobile: false,
  },
  {
    id: "customer-portal",
    duration: 6000,
    image: "/walkthrough/12_customer_portal.jpg",
    caption: "Customer reviews and approves on their phone",
    sub: "Simple. Clear. Mobile-friendly. Easy approval.",
    cursor: [
      { at: 1500, x: 50, y: 35 },
      { at: 3000, x: 50, y: 50 },
      { at: 4000, x: 50, y: 50, click: true },
    ],
    isMobile: true,
  },
  {
    id: "outro",
    duration: 3500,
    image: null,
    caption: "That's Yestimate.",
    sub: "Guided inspection. Clear review. Fast pricing. Simple approval.",
    cursor: [],
    isMobile: false,
  },
];

const TOTAL_MS = SCENES.reduce((s, sc) => s + sc.duration, 0);

// ─── Cursor state ───
function getCursorState(cursorSteps, elapsed) {
  if (!cursorSteps || !cursorSteps.length || elapsed < cursorSteps[0].at) return null;
  let cur = cursorSteps[0];
  for (const s of cursorSteps) {
    if (elapsed >= s.at) cur = s;
  }
  return {
    x: cur.x,
    y: cur.y,
    clicking: !!cur.click && elapsed >= cur.at && elapsed < cur.at + 280,
  };
}

// ─── Intro screen ───
const IntroScreen = ({ elapsed }) => (
  <div className="flex flex-col items-center justify-center h-full bg-zinc-50 px-8">
    <div style={{ opacity: elapsed > 200 ? 1 : 0, transform: elapsed > 200 ? "translateY(0)" : "translateY(8px)", transition: "all 0.4s ease" }}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4 text-center">Product Walkthrough</p>
    </div>
    <div style={{ opacity: elapsed > 500 ? 1 : 0, transform: elapsed > 500 ? "translateY(0)" : "translateY(8px)", transition: "all 0.5s ease" }}>
      <h2 style={{ fontFamily: fonts.heading }} className="text-3xl font-black tracking-tight text-zinc-900 text-center mb-3">
        See how Yestimate works
      </h2>
    </div>
    <div style={{ opacity: elapsed > 900 ? 1 : 0, transition: "opacity 0.5s ease" }}>
      <p className="text-sm text-zinc-500 text-center max-w-md leading-relaxed">
        From technician inspection to customer approval — one connected workflow.
        No software to install. Your team is up and running in minutes.
      </p>
    </div>
    <div className="flex items-center gap-5 mt-8" style={{ opacity: elapsed > 1400 ? 1 : 0, transition: "opacity 0.5s ease" }}>
      {["Inspect", "Review", "Price", "Send", "Approve"].map((s, i) => (
        <div key={i} className="flex items-center gap-2 text-xs font-semibold text-zinc-400">
          <span>{s}</span>
          {i < 4 && <ChevronRight className="w-3 h-3 text-zinc-300" />}
        </div>
      ))}
    </div>
  </div>
);

// ─── Outro screen ───
const OutroScreen = ({ elapsed, onReplay, onClose }) => (
  <div className="h-full flex flex-col items-center justify-center bg-zinc-50 px-8">
    <div style={{ opacity: elapsed > 200 ? 1 : 0, transition: "all 0.4s ease" }}>
      <h2 style={{ fontFamily: fonts.heading }} className="text-3xl font-black tracking-tight text-zinc-900 text-center mb-2">
        That's Yestimate.
      </h2>
    </div>
    <div style={{ opacity: elapsed > 600 ? 1 : 0, transition: "opacity 0.5s ease" }}>
      <p className="text-sm text-zinc-500 text-center max-w-md mb-8">
        Guided inspection. Clear review. Fast pricing. Simple customer approval.<br />
        <span className="font-semibold text-zinc-700">One system. Every step covered.</span>
      </p>
    </div>
    <div className="flex flex-wrap gap-3 justify-center mb-8" style={{ opacity: elapsed > 1000 ? 1 : 0, transition: "opacity 0.5s ease" }}>
      {["No software to install", "Online access anywhere", "Team learns in minutes", "Mobile-friendly"].map((t, i) => (
        <span key={i} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 bg-white border border-zinc-200 rounded-sm text-zinc-600">{t}</span>
      ))}
    </div>
    <div className="flex gap-3" style={{ opacity: elapsed > 1500 ? 1 : 0, transition: "opacity 0.5s ease" }}>
      <button onClick={onReplay} data-testid="walkthrough-replay"
        className="px-5 py-2.5 border border-zinc-300 text-zinc-700 text-sm font-bold rounded-sm inline-flex items-center gap-2 hover:bg-zinc-100 transition-colors">
        <RotateCcw className="w-3.5 h-3.5" /> Replay
      </button>
      <a href="/app/login" data-testid="walkthrough-start-trial"
        className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-sm inline-flex items-center gap-2 hover:bg-blue-700 transition-colors">
        Start Trial <ChevronRight className="w-3.5 h-3.5" />
      </a>
    </div>
  </div>
);

// ═══════════════════════════════════════
// MAIN WALKTHROUGH
// ═══════════════════════════════════════
const ProductWalkthrough = ({ isOpen, onClose }) => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const scene = SCENES[sceneIdx];
  const isOutro = scene.id === "outro";

  // Timer
  useEffect(() => {
    if (!isOpen) return;
    if (isOutro && elapsed >= scene.duration) return;
    const interval = setInterval(() => setElapsed((p) => p + 50), 50);
    return () => clearInterval(interval);
  }, [isOpen, isOutro, sceneIdx, elapsed, scene.duration]);

  // Scene advance
  useEffect(() => {
    if (elapsed >= scene.duration && !isOutro) {
      if (sceneIdx < SCENES.length - 1) {
        setSceneIdx((p) => p + 1);
        setElapsed(0);
      }
    }
  }, [elapsed, scene.duration, sceneIdx, isOutro]);

  // Reset on open
  useEffect(() => {
    if (isOpen) { setSceneIdx(0); setElapsed(0); }
  }, [isOpen]);

  // Preload all walkthrough images when opened
  useEffect(() => {
    if (!isOpen) return;
    SCENES.forEach((sc) => {
      if (sc.image) {
        const img = new Image();
        img.src = sc.image;
      }
    });
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const replay = useCallback(() => { setSceneIdx(0); setElapsed(0); }, []);
  const skip = useCallback(() => { setSceneIdx(SCENES.length - 1); setElapsed(0); }, []);

  if (!isOpen) return null;

  const cursorState = getCursorState(scene.cursor, elapsed);
  const totalElapsed = SCENES.slice(0, sceneIdx).reduce((s, sc) => s + sc.duration, 0) + Math.min(elapsed, scene.duration);
  const progress = totalElapsed / TOTAL_MS;

  const renderScreenContent = () => {
    if (scene.id === "intro") return <IntroScreen elapsed={elapsed} />;
    if (scene.id === "outro") return <OutroScreen elapsed={elapsed} onReplay={replay} onClose={onClose} />;

    if (scene.isMobile) {
      // Customer portal — show inside phone frame, centered
      return (
        <div className="h-full flex items-center justify-center bg-zinc-100">
          <div className="relative" style={{ width: "290px" }}>
            <div className="bg-zinc-900 rounded-[28px] p-[6px] shadow-2xl">
              <div className="rounded-[22px] overflow-hidden" style={{ height: "420px" }}>
                <img src={scene.image} alt="" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Desktop screenshot — show full screen, no cropping
    return (
      <img
        src={scene.image}
        alt=""
        className="w-full h-full object-contain"
        style={{ imageRendering: "auto", backgroundColor: "#f5f5f5" }}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="product-walkthrough">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-zinc-900/90 backdrop-blur-sm" onClick={onClose} />

      {/* Content */}
      <div className="relative w-full max-w-[1100px] mx-4">
        {/* Top controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {SCENES.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i <= sceneIdx ? "bg-blue-500" : "bg-zinc-600"}`} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            {!isOutro && (
              <button onClick={skip} data-testid="walkthrough-skip"
                className="text-xs text-zinc-400 hover:text-white font-semibold transition-colors">
                Skip
              </button>
            )}
            <button onClick={onClose} data-testid="walkthrough-close"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors">
              <X className="w-4 h-4 text-zinc-300" />
            </button>
          </div>
        </div>

        {/* Browser frame */}
        <div className="rounded-lg overflow-hidden shadow-2xl">
          {/* Chrome bar */}
          <div className="bg-zinc-800 px-4 py-2.5 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 bg-zinc-700 rounded-sm px-3 py-1 ml-2">
              <span className="text-[11px] text-zinc-400">app.yestimate.co.uk</span>
            </div>
          </div>

          {/* Screen */}
          <div className="relative bg-white overflow-hidden" style={{ height: "560px" }}>
            <div key={sceneIdx} className="h-full animate-screenIn">
              {renderScreenContent()}
            </div>

            {/* Animated cursor */}
            {cursorState && (
              <div
                className="absolute pointer-events-none z-30"
                style={{
                  left: `${cursorState.x}%`,
                  top: `${cursorState.y}%`,
                  transition: "left 0.6s cubic-bezier(0.4, 0, 0.2, 1), top 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <svg width="22" height="26" viewBox="0 0 22 26" fill="none" style={{ filter: "drop-shadow(1px 2px 4px rgba(0,0,0,0.35))" }}>
                  <path d="M5 2L18 13L11 14.5L8 22L5 2Z" fill="white" stroke="#111" strokeWidth="1.3" strokeLinejoin="round" />
                </svg>
                {cursorState.clicking && (
                  <div className="absolute left-2 top-2 w-7 h-7 -ml-3.5 -mt-3.5">
                    <div className="w-full h-full rounded-full bg-blue-500/40 animate-clickRipple" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Caption */}
        <div className="mt-5 text-center">
          <p className="text-sm font-semibold text-white" style={{ fontFamily: fonts.body }}>{scene.caption}</p>
          <p className="text-xs text-zinc-400 mt-1">{scene.sub}</p>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-0.5 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full"
            style={{ width: `${progress * 100}%`, transition: "width 0.1s linear" }}
          />
        </div>
      </div>

      {/* CSS */}
      <style>{`
        @keyframes screenIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-screenIn { animation: screenIn 0.35s ease-out; }
        @keyframes clickRipple { 0% { transform: scale(0.3); opacity: 0.6; } 100% { transform: scale(2.5); opacity: 0; } }
        .animate-clickRipple { animation: clickRipple 0.4s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default ProductWalkthrough;
